import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const TechnicalFeatures = () => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(null);
  
  const technicalFeatures = [
    {
      id: 'hash',
      name: t('technicalFeatures.hash.name'),
      icon: '🔢',
      description: t('technicalFeatures.hash.description'),
      fullDescription: t('technicalFeatures.hash.fullDescription'),
      benefits: [
        t('technicalFeatures.hash.benefit1'),
        t('technicalFeatures.hash.benefit2'),
        t('technicalFeatures.hash.benefit3'),
        t('technicalFeatures.hash.benefit4')
      ]
    },
    {
      id: 'timestamp',
      name: t('technicalFeatures.timestamp.name'),
      icon: '⏰',
      description: t('technicalFeatures.timestamp.description'),
      fullDescription: t('technicalFeatures.timestamp.fullDescription'),
      benefits: [
        t('technicalFeatures.timestamp.benefit1'),
        t('technicalFeatures.timestamp.benefit2'),
        t('technicalFeatures.timestamp.benefit3'),
        t('technicalFeatures.timestamp.benefit4')
      ]
    },
    {
      id: 'icp',
      name: t('technicalFeatures.icp.name'),
      icon: '🏛️',
      description: t('technicalFeatures.icp.description'),
      fullDescription: t('technicalFeatures.icp.fullDescription'),
      benefits: [
        t('technicalFeatures.icp.benefit1'),
        t('technicalFeatures.icp.benefit2'),
        t('technicalFeatures.icp.benefit3'),
        t('technicalFeatures.icp.benefit4')
      ]
    },
    {
      id: 'govbr',
      name: t('technicalFeatures.govbr.name'),
      icon: '🇧🇷',
      description: t('technicalFeatures.govbr.description'),
      fullDescription: t('technicalFeatures.govbr.fullDescription'),
      benefits: [
        t('technicalFeatures.govbr.benefit1'),
        t('technicalFeatures.govbr.benefit2'),
        t('technicalFeatures.govbr.benefit3'),
        t('technicalFeatures.govbr.benefit4')
      ]
    }
  ];

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
      {technicalFeatures.map((feature, index) => (
        <motion.div
          key={feature.id}
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ delay: index * 0.1 }}
          viewport={{ once: true }}
          className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-blue-500 transition-all"
        >
          <div className="text-4xl mb-4 text-center">{feature.icon}</div>
          <h4 className="text-xl font-bold text-gray-900 mb-2 text-center">{feature.name}</h4>
          <p className="text-sm text-gray-600 text-center mb-4">{feature.description}</p>

          <button
            onClick={() => setExpanded(expanded === feature.id ? null : feature.id)}
            className="w-full bg-blue-50 hover:bg-blue-100 text-blue-700 py-2 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2 text-sm"
          >
            <span>{expanded === feature.id ? t('technicalFeatures.hide') : t('technicalFeatures.learnMore')}</span>
            {expanded === feature.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          <AnimatePresence>
            {expanded === feature.id && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="mt-4 pt-4 border-t border-gray-200"
              >
                <p className="text-sm text-gray-700 mb-3 leading-relaxed">{feature.fullDescription}</p>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2 text-sm">{t('technicalFeatures.benefits')}:</h5>
                  <ul className="space-y-1">
                    {feature.benefits.map((benefit, i) => (
                      <li key={i} className="text-xs text-gray-600 flex items-start space-x-2">
                        <span className="text-blue-600 mt-0.5">✓</span>
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      ))}
    </div>
  );
};

export default TechnicalFeatures;
